import { Drawer, Button } from 'antd';
import {
    IdcardOutlined, CreditCardOutlined, DollarOutlined,
    FileTextOutlined, StopOutlined,
} from '@ant-design/icons';
import { BRAND } from '../constants';
import { renderNa, renderAmount, statusToColor, statusPillStyle } from '../utils';
import { getRegion } from '../../../../components/regionWrapper';
import { Region } from '../../../../utils/commonInterfaces';

interface UserDetailsDrawerProps {
    open: boolean;
    user: any | null;
    onClose: () => void;
    onSuspend: (id: string) => void;
}

interface Field {
    label: string;
    labelAr?: string;
    value: React.ReactNode;
}

const DetailRow = ({ label, labelAr, value }: Field) => (
    <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, padding: '11px 0', borderBottom: '1px solid #F2F5F5',
    }}>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: '#5b6b70', fontWeight: 500 }}>{label}</div>
            {labelAr && <div dir="rtl" style={{ fontSize: 12, color: '#9aa7ac' }}>{labelAr}</div>}
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: BRAND.ink, textAlign: 'right', wordBreak: 'break-word' }}>
            {value}
        </div>
    </div>
);

const Section = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
    <div style={{
        background: '#fff', border: '1px solid #EEF3F3', borderRadius: 14,
        padding: '6px 16px 10px', marginBottom: 14,
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0 6px' }}>
            <span style={{
                width: 28, height: 28, borderRadius: 8, background: BRAND.tealSoft,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: BRAND.teal,
            }}>
                {icon}
            </span>
            <span style={{ fontSize: 12, fontWeight: 800, color: BRAND.ink, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {title}
            </span>
        </div>
        {children}
    </div>
);

// Right-side drawer showing everything related to a single user, with a Suspend action.
export default function UserDetailsDrawer({ open, user, onClose, onSuspend }: UserDetailsDrawerProps) {
    const region = getRegion();
    const statusColor = statusToColor(user?.status);
    const statusTextColor = statusPillStyle(user?.status).color;
    const initials = (user?.name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p: string) => p[0]?.toUpperCase())
        .join('') || '—';

    return (
        <Drawer
            visible={open}
            onClose={onClose}
            placement="right"
            width={460}
            closable={false}
            destroyOnClose
            className="np-user-drawer"
            bodyStyle={{ padding: 0, background: '#F6FAFA' }}
            headerStyle={{ display: 'none' }}
        >
            {/* Hero header */}
            <div style={{
                background: BRAND.heroGradient, padding: '26px 24px 26px', position: 'relative',
                overflow: 'hidden', flexShrink: 0,
            }}>
                <div style={{
                    position: 'absolute', top: -50, right: -30, width: 160, height: 160,
                    borderRadius: '50%', background: 'rgba(255,255,255,0.10)', pointerEvents: 'none',
                }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{
                            width: 54, height: 54, borderRadius: 16, flexShrink: 0,
                            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 20, fontWeight: 800, color: '#fff',
                        }}>
                            {initials}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 19, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                                {renderNa(user?.name)}
                            </div>
                            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.82)', marginTop: 2 }}>
                                Staff ID · {renderNa(user?.staff_id)}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 14px',
                            borderRadius: 999, fontSize: 12.5, fontWeight: 800, background: '#fff',
                            color: statusTextColor, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
                            {renderNa(user?.status)}
                        </span>
                        {user?.isSuspended ? (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                                borderRadius: 999, fontSize: 12.5, fontWeight: 800,
                                background: '#fff', color: '#c0392b',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            }}>
                                <StopOutlined /> Suspended
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Body */}
            <div style={{ padding: 16, flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
                <Section icon={<IdcardOutlined />} title="Identity">
                    <DetailRow label="User ID" value={renderNa(user?.userID)} />
                    <DetailRow label="Company" value={renderNa(user?.company)} />
                    <DetailRow label="Staff ID" value={renderNa(user?.staff_id)} />
                    <DetailRow label="National ID" labelAr="رقم الهويه" value={renderNa(user?.nationalID)} />
                    {region === Region.EG && <DetailRow label="Bank Account Number" value={renderNa(user?.bankAccountNumber)} />}
                </Section>

                <Section icon={<DollarOutlined />} title="Financials">
                    <DetailRow label="Next Month Due Amount" labelAr="المبلغ المستحق للشهر القادم" value={renderAmount(user?.nextMonthDueAmount)} />
                    <DetailRow label="Remaining Outstanding Amounts" labelAr="المبالغ المتبقية/المستحقة" value={renderAmount(user?.outstandingAmount)} />
                    {region === Region.SE ?
                        <>
                            <DetailRow label="Paid Amount" labelAr="المبلغ المدفوع" value={renderAmount(user?.paidAmount)} />
                            <DetailRow label="Early Settlement Amount" labelAr="مبلغ السداد المبكر" value={renderAmount(user?.earlySettlementAmount)} />
                        </>
                        : null
                    }
                </Section>

                <Section icon={<FileTextOutlined />} title="Requests">
                    {region === Region.SE && <DetailRow label="Current Active Requests" labelAr="الطلبات النشطة الحالية" value={renderNa(user?.activeRequests)} />}
                    <DetailRow label="Total Requests Transferred" labelAr="إجمالي الطلبات المحوّلة" value={renderNa(user?.totalRequestsTransferred)} />
                    <DetailRow label="Total Requested Amount (Incl. Cancelled & Processing)" labelAr="إجمالي المبلغ المطلوب" value={renderAmount(user?.totalRequestedAmountAllStatuses)} />
                    <DetailRow label="Total Requested Amount Transferred" labelAr="إجمالي المبلغ المطلوب المحوّل" value={renderAmount(user?.totalRequestedAmountTransferred)} />
                    {region === Region.SE ?
                        <>
                            <DetailRow label="Remaining Installments (Months)" labelAr="الأقساط المتبقية (بالأشهر)" value={renderNa(user?.remainingInstallments)} />
                            <DetailRow label="Requests Installments (Months)" labelAr="عدد أقساط الطلب (بالأشهر)" value={renderNa(user?.requestsInstallments)} />
                        </>
                        : null
                    }
                </Section>

                <Section icon={<CreditCardOutlined />} title="Suspension">
                    <DetailRow
                        label="Suspension Flag"
                        value={
                            <span style={{ color: user?.isSuspended ? BRAND.greenDark : '#8fa3ae' }}>
                                {user?.isSuspended ? 'Yes' : 'No'}
                            </span>
                        }
                    />
                    <DetailRow label="Suspension Reason" value={user?.isSuspended && user?.suspensionReason ? user.suspensionReason : 'N/A'} />
                </Section>
            </div>

            {/* Footer action */}
            <div style={{
                flexShrink: 0, background: '#fff', borderTop: '1px solid #EEF3F3',
                padding: '14px 16px', display: 'flex', gap: 10,
            }}>
                <Button onClick={onClose} style={{ height: 42, borderRadius: 10, flex: 1, fontWeight: 600 }}>
                    Close
                </Button>

                {
                    region === Region.SE && (
                        user?.isSuspended ? (
                            <Button disabled style={{ height: 42, borderRadius: 10, flex: 1, fontWeight: 600 }}>
                                Already Suspended
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                className="np-primary-btn"
                                onClick={() => user?.userID && onSuspend(user.userID)}
                                style={{ height: 42, borderRadius: 10, flex: 1, fontWeight: 700, border: 'none' }}
                            >
                                Suspend User
                            </Button>
                        )
                    )
                }
            </div>
        </Drawer>
    );
}
